import { useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Input,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  theme,
} from "antd";
import {
  ArrowRightOutlined,
  DeleteOutlined,
  DownOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ProcessChoice, ProcessStatus } from "../../api";
import { labelFor, STATUS_KIND_HINT, STATUS_KIND_LABEL } from "./terms";
import TransitionRules, { type TransitionDraft } from "./TransitionRules";
import type { FieldRef } from "./types";

const { Text, Title } = Typography;

type Props = {
  statuses: ProcessStatus[];
  onChange: (statuses: ProcessStatus[]) => void;
  /** Per-move restrictions, held apart from the statuses because a rule belongs
   *  to the EDGE: nesting it under either end would lose it the moment somebody
   *  rearranged which state owned the move. */
  transitions?: TransitionDraft[];
  onTransitionsChange?: (transitions: TransitionDraft[]) => void;
  groups?: Array<{ id: number; name: string }>;
  fields?: FieldRef[];
  conditionOps?: string[];
  conditionOrderOps?: string[];
  statusKinds: ProcessChoice[];
  systemActions: ProcessChoice[];
  disabled?: boolean;
};

/** Next free handle for a state being added. Negative ids are handles for rows
 *  that do not exist yet, so another state can point at one before it is saved. */
const nextHandle = (statuses: ProcessStatus[]) =>
  Math.min(0, ...statuses.map((s) => s.id ?? 0)) - 1;

/** Colour per kind, matching the canvas lanes so the two read as one thing. */
const KIND_COLOUR: Record<string, string> = {
  Draft: "default",
  DataEntry: "blue",
  Approval: "purple",
  Active: "green",
  Closed: "default",
  Other: "orange",
};

/**
 * The lifecycle: what states a case can be in, and where each one leads.
 *
 * This was a five-column table scrolling horizontally at 980px inside a drawer,
 * which made the one genuinely important idea invisible. That idea is **kind
 * versus name**: behaviour keys off the kind -- Draft is where a case starts,
 * Approval is the transition that opens the signature slots -- while the name
 * is only what people read, which is what lets a client rename "Approval" into
 * Russian without the engine noticing. Three production bugs have been someone
 * looking a status up by name.
 *
 * So each state is now a card that leads with its kind and says what the kind
 * means, and the transitions are chips you can read as sentences ("Draft goes
 * to → In review") rather than multi-selects in a cell. The information is the
 * same; what changed is that the dangerous part is now the visible part.
 */
const LifecycleEditor = ({
  statuses,
  onChange,
  transitions,
  onTransitionsChange,
  groups = [],
  fields = [],
  conditionOps = [],
  conditionOrderOps = [],
  statusKinds,
  systemActions,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [openId, setOpenId] = useState<number | null>(null);

  const update = (index: number, patch: Partial<ProcessStatus>) =>
    onChange(statuses.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const remove = (index: number) => {
    const removed = statuses[index];
    // Drop every reference to it too, otherwise the save is refused for
    // pointing at a state that is no longer in the list.
    onChange(
      statuses
        .filter((_, i) => i !== index)
        .map((s) => ({
          ...s,
          next_auto_status:
            s.next_auto_status === removed.id ? null : s.next_auto_status,
          next_possible_statuses: s.next_possible_statuses.filter(
            (ref) => ref !== removed.id
          ),
        }))
    );
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= statuses.length) return;
    const next = [...statuses];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const add = () => {
    const handle = nextHandle(statuses);
    onChange([
      ...statuses,
      {
        id: handle,
        name: "",
        kind: "Other",
        action_title: "",
        system_actions: [],
        next_auto_status: null,
        next_possible_statuses: [],
      },
    ]);
    setOpenId(handle);
  };

  const nameOf = (id: number | null | undefined) =>
    statuses.find((s) => s.id === id)?.name?.trim() || t("Unnamed state");

  const targets = (index: number) =>
    statuses
      .map((s, i) => ({ status: s, i }))
      .filter(({ i }) => i !== index)
      .map(({ status }) => ({
        value: status.id as number,
        label: status.name || t("Unnamed state"),
      }));

  const kindCount = (kind: string) =>
    statuses.filter((s) => s.kind === kind).length;
  const draftProblem = kindCount("Draft") !== 1;
  const approvalProblem = kindCount("Approval") > 1;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message={t("Names are yours; kinds are the engine's")}
        description={t(
          "What a state means to the system comes from its kind, never its name — so you can rename or translate any of these freely. Setting the wrong kind is the one change here that alters behaviour."
        )}
      />

      {(draftProblem || approvalProblem) && (
        <Alert
          type="warning"
          showIcon
          message={
            draftProblem
              ? t(
                  "Exactly one state has to be of kind ‘Being written’ — that is where a new case starts."
                )
              : t("Only one state can be of kind ‘Being signed off’.")
          }
        />
      )}

      {statuses.length === 0 && (
        <Empty description={t("This process has no states yet.")} />
      )}

      {statuses.map((status, index) => {
        const open = openId === status.id;
        const nexts = status.next_possible_statuses ?? [];
        return (
          <div
            key={status.id ?? index}
            style={{
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${
                open ? token.colorPrimaryBorder : token.colorBorderSecondary
              }`,
              background: token.colorBgContainer,
              overflow: "hidden",
            }}
          >
            {/* Summary line: the state, its kind, and where it can go. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                cursor: "pointer",
              }}
              onClick={() => setOpenId(open ? null : (status.id as number))}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                  flex: "0 0 auto",
                  borderRadius: "50%",
                  border: `1px solid ${token.colorBorder}`,
                  fontSize: 11,
                  color: token.colorTextTertiary,
                }}
              >
                {index + 1}
              </span>

              <Title level={5} style={{ margin: 0, flex: "0 1 auto" }} ellipsis>
                {status.name?.trim() || t("Unnamed state")}
              </Title>

              <Tooltip title={t(labelFor(STATUS_KIND_HINT, status.kind))}>
                <Tag color={KIND_COLOUR[status.kind] ?? "default"}>
                  {t(labelFor(STATUS_KIND_LABEL, status.kind))}
                </Tag>
              </Tooltip>

              <div style={{ flex: 1, minWidth: 0 }}>
                {nexts.length > 0 && (
                  <Space size={4} wrap>
                    <ArrowRightOutlined
                      style={{ color: token.colorTextQuaternary, fontSize: 11 }}
                    />
                    {nexts.map((ref) => (
                      <Tag
                        key={ref}
                        style={{ marginInlineEnd: 0, fontSize: 11 }}
                        bordered={false}
                      >
                        {nameOf(ref)}
                      </Tag>
                    ))}
                  </Space>
                )}
                {status.next_auto_status != null && (
                  <Tooltip
                    title={t(
                      "Once the sign-offs here are done, the case moves on by itself."
                    )}
                  >
                    <Tag
                      icon={<ThunderboltOutlined />}
                      color="gold"
                      style={{ marginInlineStart: 6, fontSize: 11 }}
                    >
                      {t("then automatically {{name}}", {
                        name: nameOf(status.next_auto_status),
                      })}
                    </Tag>
                  </Tooltip>
                )}
              </div>

              {(status.in_use ?? 0) > 0 && (
                <Tooltip
                  title={t("Cases are sitting in this state right now.")}
                >
                  <Tag bordered={false}>
                    {t("{{count}} in use", { count: status.in_use })}
                  </Tag>
                </Tooltip>
              )}

              <Space size={0} onClick={(e) => e.stopPropagation()}>
                <Button
                  size="small"
                  type="text"
                  icon={<UpOutlined />}
                  disabled={disabled || index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={t("Move earlier")}
                />
                <Button
                  size="small"
                  type="text"
                  icon={<DownOutlined />}
                  disabled={disabled || index === statuses.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={t("Move later")}
                />
                <Popconfirm
                  title={t("Remove this state?")}
                  description={t(
                    "Anything pointing at it stops pointing at it. A state that cases are sitting in cannot be removed."
                  )}
                  okText={t("Remove")}
                  cancelText={t("Cancel")}
                  onConfirm={() => remove(index)}
                >
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={disabled}
                    aria-label={t("Remove this state")}
                  />
                </Popconfirm>
              </Space>
            </div>

            {open && (
              <div
                style={{
                  padding: "0 12px 12px",
                  borderTop: `1px solid ${token.colorBorderSecondary}`,
                  display: "grid",
                  gap: 12,
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  paddingTop: 12,
                }}
              >
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("What is it called?")}
                  </Text>
                  <Input
                    style={{ marginTop: 4 }}
                    value={status.name}
                    disabled={disabled}
                    status={status.name?.trim() ? undefined : "error"}
                    placeholder={t("e.g. In review")}
                    onChange={(e) => update(index, { name: e.target.value })}
                  />
                </div>

                <div>
                  <Tooltip title={t(labelFor(STATUS_KIND_HINT, status.kind))}>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, borderBottom: "1px dotted" }}
                    >
                      {t("What does it mean to the engine?")}
                    </Text>
                  </Tooltip>
                  <Select
                    style={{ width: "100%", marginTop: 4 }}
                    value={status.kind}
                    disabled={disabled}
                    onChange={(v) => update(index, { kind: v })}
                    options={statusKinds.map((k) => ({
                      value: k.value,
                      label: t(labelFor(STATUS_KIND_LABEL, k.value)),
                    }))}
                  />
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block", marginTop: 4 }}
                  >
                    {t(labelFor(STATUS_KIND_HINT, status.kind))}
                  </Text>
                </div>

                <div>
                  <Tooltip
                    title={t(
                      "The wording on the button somebody presses to move a case into this state."
                    )}
                  >
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, borderBottom: "1px dotted" }}
                    >
                      {t("Wording on the button")}
                    </Text>
                  </Tooltip>
                  <Input
                    style={{ marginTop: 4 }}
                    value={status.action_title}
                    disabled={disabled}
                    placeholder={t("e.g. Send for review")}
                    onChange={(e) =>
                      update(index, { action_title: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("Where can it go next?")}
                  </Text>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: "100%", marginTop: 4 }}
                    placeholder={t("Nowhere — this state ends the case")}
                    value={status.next_possible_statuses}
                    disabled={disabled}
                    onChange={(v) => update(index, { next_possible_statuses: v })}
                    options={targets(index)}
                  />
                </div>

                {onTransitionsChange && nexts.length > 0 && (
                  <TransitionRules
                    fromId={status.id ?? 0}
                    targets={nexts}
                    nameOf={nameOf}
                    transitions={transitions ?? []}
                    onChange={onTransitionsChange}
                    groups={groups}
                    fields={fields}
                    ops={conditionOps}
                    orderOps={conditionOrderOps}
                    disabled={disabled}
                  />
                )}

                <div>
                  <Tooltip
                    title={t(
                      "Used when this state finishes on its own — when the last sign-off in it is given, the case moves straight here without anybody pressing anything."
                    )}
                  >
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, borderBottom: "1px dotted" }}
                    >
                      {t("Then move on automatically to")}
                    </Text>
                  </Tooltip>
                  <Select
                    allowClear
                    style={{ width: "100%", marginTop: 4 }}
                    placeholder={t("Nothing automatic")}
                    value={status.next_auto_status ?? undefined}
                    disabled={disabled}
                    onChange={(v) => update(index, { next_auto_status: v ?? null })}
                    options={targets(index)}
                  />
                </div>

                <div>
                  <Tooltip
                    title={t(
                      "Which of the built-in actions are offered while a case is in this state."
                    )}
                  >
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, borderBottom: "1px dotted" }}
                    >
                      {t("What can people do here?")}
                    </Text>
                  </Tooltip>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: "100%", marginTop: 4 }}
                    placeholder={t("Nothing special")}
                    value={status.system_actions}
                    disabled={disabled}
                    onChange={(v) => update(index, { system_actions: v })}
                    options={systemActions.map((a) => ({
                      value: a.value,
                      label: t(a.label),
                    }))}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}

      <Button icon={<PlusOutlined />} disabled={disabled} onClick={add} block>
        {t("Add a state")}
      </Button>
    </Space>
  );
};

export default LifecycleEditor;
